import { Slot, SlotProps } from "@radix-ui/react-slot";
import clsx from "clsx";
import { AnchorHTMLAttributes, forwardRef, type ForwardedRef } from "react";

import styles from "./item.module.css";

/**
 *  When prop `isActive` is true, className `active` is added to element.
 *  When react-router-dom NavLink is active, className `active` is added to element.
 *  - https://reactrouter.com/en/main/components/nav-link#default-active-class
 */
const activeClassName = "active";

type PropsAnchor = AnchorHTMLAttributes<HTMLAnchorElement>;

type PropsSlot = SlotProps;

type Props<TAsChild extends boolean = false> = {
    asChild?: TAsChild;
    isActive?: boolean;
} & (TAsChild extends true ? PropsSlot : PropsAnchor);

type RefProp<TAsChild extends boolean = false> = TAsChild extends true ? never : ForwardedRef<HTMLAnchorElement>;

const ItemInner = <TAsChild extends boolean = false>(props: Props<TAsChild>, ref: ForwardedRef<TAsChild>) => {
    const { asChild = false, children, isActive, className, ...otherProps } = props;

    const Component = asChild ? Slot : "a";

    return (
        <Component
            {...otherProps}
            ref={asChild ? undefined : (ref as RefProp<true>)}
            className={clsx(styles.item, className, { [activeClassName]: isActive })}
        >
            {children}
        </Component>
    );
};

ItemInner.displayName = "Item";

const Item = forwardRef(ItemInner) as <TAsChild extends boolean = false>(
    props: Props<TAsChild> & { ref?: RefProp<TAsChild> }
) => ReturnType<typeof ItemInner>;

export { Item };
export type { Props as ItemProps };
